<template>
    <!-- <Teleport to="app"> -->
        <div class="
            flex justify-center bg-white shadow-card dark:bg-gray-950 fixed bottom-0 left-0 w-full h-20 box-content pb-[env(safe-area-inset-bottom)] border-t dark:border-gray-700 z-[100]
        ">
            <div
                ref="navGrid"
                class="
                    relative grid grid-cols-5 justify-between gap-4 w-full max-w-lg px-6 pb-4
                "
                :class="`grid-cols-${nav.length}`"
            >
                <!--
                    Animated active-tab indicator (HAP-1888, parent HAP-1884 Phase 1.2):
                    a small rounded rail that springs/slides to the active tab. Measured
                    off the live button DOM (handles the wider raised center tab + gaps),
                    transform-only so it never reflows. CSS spring transition pulls from
                    the P0.2 motion tokens; the global reduced-motion guard collapses it.
                -->
                <span
                    class="tab-indicator pointer-events-none absolute top-0 h-[3px] w-7 -ml-3.5 rounded-full bg-blue-500"
                    :style="{
                        transform: `translateX(${indicator.x}px)`,
                        opacity: indicator.visible ? 1 : 0,
                    }"
                    aria-hidden="true"
                />
                <button
                    v-for="section, i in nav" :key="section.title"
                    v-press
                    :ref="el => (navButtons[i] = el)"
                    :class="[
                        $route.path.includes(section.path) && section.path != '/' ? '!text-blue-500 dark:!text-blue-500' : '',
                        i == 2 ? '-mt-4 rounded-full shrink-0 aspect-square border-t dark:border-gray-700 text-xl p-4 bg-white dark:bg-gray-950' : 'text-lg',
                        section.featured ? 'glassy-featured' : ''
                    ]"
                    class="flex flex-col items-center justify-center leading-normal dark:text-gray-400"
                    @click="setTab(section)"
                    :dataTest="section.title"
                >
                    <div><i :class="`fad fa-${section.icon}`" /></div>
                    <div class="text-2xs">{{ section.title }}</div>
                </button>
            </div>
        </div>
    <!-- </Teleport> -->
</template>

<script>
import login from './login.vue'
import InstallPrompt from './installPrompt.vue'
export default {
    components: { login, InstallPrompt },
    inject: ['store','darkMode'],
    data() {
        return {
            navHidden: false,
            navButtons: [],
            indicator: { x: 0, visible: false },
        }
    },
    methods: {
        // Position the active-tab rail over the centre of the current tab. Called
        // on mount and whenever the route changes. We measure the live button so
        // the rail tracks the wider raised centre tab + the grid gaps exactly.
        moveIndicator() {
            const i = this.activeIndex
            const btn = i >= 0 ? this.navButtons[i] : null
            const grid = this.$refs.navGrid
            // Hidden (navHidden) or no active tab → just fade the rail out in place.
            if (!btn || !grid || !grid.offsetWidth) {
                this.indicator.visible = false
                return
            }
            this.indicator.x = btn.offsetLeft + btn.offsetWidth / 2
            this.indicator.visible = true
        },
        setTab(section) {
            // This will make the "rep" tab take you back to your current opportunity.
            let updatingOpportunity = this.$route.path.includes('opportunities') && this.$route.params?.id
            if (updatingOpportunity && section.path != '/') this.store.currentOpportunityPath = this.$route.path

            // this.store.openType = 'slideUp'
            this.$router.push(section.path)

            // This way if they hit "rep" a second time it will return them to the home screen
            if (section.path == this.store.currentOpportunityPath) this.store.currentOpportunityPath = null
        }
    },
    mounted() {
        this.$nextTick(() => this.moveIndicator())
        // Reposition the rail if the viewport resizes (gaps/widths shift).
        this._onResize = () => this.moveIndicator()
        window.addEventListener('resize', this._onResize, { passive: true })
    },
    beforeUnmount() {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    watch: {
        // Slide the rail to the new tab (and re-measure when the nav set swaps,
        // e.g. entering the event app where `nav` returns a different list).
        '$route.path'() {
            this.$nextTick(() => this.moveIndicator())
        },
        navHidden() {
            this.$nextTick(() => this.moveIndicator())
        },
    },
    computed: {
        // Index of the tab whose path the current route is under (mirrors the
        // active-colour class). -1 when none match (e.g. the home screen).
        activeIndex() {
            return this.nav.findIndex(
                (section) => this.$route.path.includes(section.path) && section.path != '/'
            )
        },
        nav() {
            if (this.$route.path.match('/events/.+/app')) {
                return [
                    {title: 'Speakers', icon: 'keynote', path: `/events/${this.$route.params.id}/app/speakers`},
                    // {title: 'Extras', icon: 'plus-circle', path: `/events/${this.$route.params.id}/app/extras`},
                    {title: 'Hymnal', icon: 'book', path: `/events/${this.$route.params.id}/app/hymns`},
                    {title: 'Schedule', icon: 'calendar', path: `/events/${this.$route.params.id}/app/schedule`},
                    {title: 'Q&A', icon: 'question', path: `/events/${this.$route.params.id}/app/qa`},
                    {title: 'Home', icon: 'fa fa-sign-out-alt', path: `/`},
                ]
            }
            return [
                { title: 'Resources', path: '/resources', icon: 'film' },
                { title: 'Network', path: '/network/index', icon: 'map-marked-alt' },
                // { title: 'Store', path: '/products', icon: 'book' },
                // (this.store.user?.member?.roles?.includes('admin') ? { title: 'BDP', path: '/events/65f06e8db04a89346884a83b/app', icon: 'podium', featured: true } : { title: 'Events', path: '/events', icon: 'podium' }),
                { title: 'Events', path: '/events', icon: 'podium' },
                // { title: 'Podcasts', path: '/podcasts', icon: 'microphone' },
                // { title: 'Study', path: '/study', icon: 'sparkles' },
                { title: 'Store', path: '/store', icon: 'book' },
                { title: 'Podcast', path: '/podcasts', icon: 'microphone' },
            ]
        }
    }
}
</script>

<style scoped>
/* Active-tab rail: spring-slide horizontally, quick fade in/out. Pulls the P0.2
   motion tokens; the global @media (prefers-reduced-motion) guard collapses the
   durations so it snaps into place with no motion. */
.tab-indicator {
    transition:
        transform var(--motion-duration-base) var(--motion-ease-spring),
        opacity var(--motion-duration-fast) var(--motion-ease-standard);
    will-change: transform;
}
.glassy-featured {
    background: rgba(255, 255, 255, 0.25);
    box-shadow: 0 4px 24px 0 rgba(31, 38, 135, 0.12);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1.5px solid rgba(255, 255, 255, 0.35);
    border-radius: 1.5rem;
    transition: box-shadow 0.2s, background 0.2s, border 0.2s;
    position: relative;
    z-index: 1;
}
.glassy-featured:after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(120deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.10) 100%);
}
.dark .glassy-featured {
    background: rgba(30, 41, 59, 0.25);
    border: 1.5px solid rgba(148, 163, 184, 0.25);
    box-shadow: 0 4px 24px 0 rgba(30, 41, 59, 0.18);
}
</style>
